import { calculateEventLateFlags } from "@src/pages/Dashboard/hooks/useEventLateFlags";
import { shouldShowLateIcon } from "@src/pages/Dashboard/hooks/useTimelineEvent";

/**
 * Calcula qual data de início deve ser exibida
 *
 * REGRAS:
 * - CANCELED/NON_EXECUTION: não exibe data
 * - AWAITING_END: não exibe data prevista (oneshot, só mostra quando tiver executionEnd)
 * - Se tem executionStart ou executionEnd: usa executionStart
 * - Senão: usa plannedExecutionStart
 */
function calculateDisplayStartDate(
  status: Sentinel.Enumerators.ProcessExecutionStatus,
  executionStart: string | null | undefined,
  executionEnd: string | null | undefined,
  plannedExecutionStart: string | null | undefined
): string | null {
  if (status === "CANCELED" || status === "NON_EXECUTION") return null;
  if (status === "AWAITING_END") return null;
  if (executionStart || executionEnd) return executionStart ?? null;
  return plannedExecutionStart ?? null;
}

/**
 * Calcula qual data de fim deve ser exibida
 *
 * REGRAS:
 * - CANCELED/NON_EXECUTION: não exibe data
 * - AWAITING_END: só mostra se tiver executionEnd (oneshot, não mostra data prevista)
 * - Se tem executionStart ou executionEnd: usa executionEnd
 * - Senão: usa plannedExecutionEnd
 */
function calculateDisplayEndDate(
  status: Sentinel.Enumerators.ProcessExecutionStatus,
  executionStart: string | null | undefined,
  executionEnd: string | null | undefined,
  plannedExecutionEnd: string | null | undefined
): string | null {
  if (status === "CANCELED" || status === "NON_EXECUTION") return null;
  if (status === "AWAITING_END") return executionEnd ?? null; // Oneshot: só mostra executionEnd quando tiver
  if (executionStart || executionEnd) return executionEnd ?? null;
  return plannedExecutionEnd ?? null;
}

/**
 * Calcula se deve marcar fundo do início baseado nos flags
 */
function getMarkStartBackground(
  flags: ReturnType<typeof calculateEventLateFlags>,
): boolean {
  return flags.notStartedAndStartPassed || flags.startedLate;
}

/**
 * Calcula se deve marcar fundo do fim baseado nos flags
 * Se passou do fim previsto → pinta END (usa notStartedAndEndPassed)
 */
function getMarkEndBackground(
  flags: ReturnType<typeof calculateEventLateFlags>,
): boolean {
  return flags.notStartedAndEndPassed || flags.onlyEndLate || flags.endedLate;
}

/**
 * Prepara um evento com props de renderização calculadas
 *
 * Esta função centraliza TODA a lógica de cálculo de:
 * - Datas para exibir
 * - Flags de atraso (ícone e fundos)
 *
 * REGRAS DE NEGÓCIO:
 *
 * 1. DISPLAY DATES:
 *    - CANCELED: não exibe datas
 *    - AWAITING_END: oneshot - não exibe datas previstas, só mostra executionEnd quando tiver
 *    - Se tem executionStart/End: usa datas reais
 *    - Senão: usa datas previstas
 *
 * 2. LATE ICON (showLateIcon):
 *    - CANCELED: nunca mostra
 *    - STARTED_LATE/DELAYED/COMPLETED_LATE: sempre mostra
 *    - COMPLETED_EARLY/COMPLETED: só mostra se finalizou atrasado
 *      (não mostra se iniciou atrasado mas finalizou no prazo)
 *    - AWAITING_END: mostra se passou do plannedExecutionEnd sem executionEnd
 *
 * 3. START BACKGROUND (markStartBackground):
 *    - Marca se: processo não iniciou e passou do início previsto OU iniciou atrasado
 *    - AWAITING_END: nunca marca (não tem executionStart)
 *
 * 4. END BACKGROUND (markEndBackground):
 *    - Marca se: processo não iniciou e passou do fim previsto OU
 *                só o fim está atrasado OU finalizou atrasado
 *    - AWAITING_END: marca se passou do plannedExecutionEnd sem executionEnd
 *                    OU se tem executionEnd e está atrasado
 */
export function prepareEventForDisplay(
  event: Sentinel.Timeline.Event,
): Sentinel.Timeline.Event {
  if (event.displayProps) {
    return event;
  }

  const status = event.status;
  const isNotExecuted = status === "CANCELED" || status === "NON_EXECUTION";

  // Calcular flags de atraso uma vez
  const lateFlags = calculateEventLateFlags(event);

  // Calcular datas para exibir
  const displayStartDate = calculateDisplayStartDate(
    status,
    event.executionStart,
    event.executionEnd,
    event.plannedExecutionStart,
  );

  const displayEndDate = calculateDisplayEndDate(
    status,
    event.executionStart,
    event.executionEnd,
    event.plannedExecutionEnd,
  );

  // Calcular se mostra ícone de atraso
  const showLateIcon = shouldShowLateIcon(event, lateFlags);

  // Calcular se marca fundos
  let markStartBackground = false;
  let markEndBackground = false;

  if (!isNotExecuted) {
    switch (status) {
      case "AWAITING_END":
        // AWAITING_END: lógica específica oneShot
        markEndBackground = lateFlags.awaitingEndLate;
        break;

      case "STARTED_LATE":
      case "DELAYED":
        // Status que indicam atraso no início: força START
        markStartBackground = true;
        markEndBackground = getMarkEndBackground(lateFlags);
        break;

      case "COMPLETED_LATE":
        // Status que indica atraso no fim: força END
        markStartBackground = getMarkStartBackground(lateFlags);
        markEndBackground = true;
        break;

      case "STARTED_EARLY":
        // Status que indica início antecipado: não marca START
        markEndBackground = getMarkEndBackground(lateFlags);
        break;

      case "COMPLETED_EARLY":
        // Status que indica fim antecipado: não marca END
        markStartBackground = getMarkStartBackground(lateFlags);
        break;

      default:
        // Outros status: calcular normalmente
        markStartBackground = getMarkStartBackground(lateFlags);
        markEndBackground = getMarkEndBackground(lateFlags);
        break;
    }
  }

  return {
    ...event,
    displayProps: {
      displayStartDate,
      displayEndDate,
      showLateIcon,
      markStartBackground,
      markEndBackground,
    },
  };
}
